import React, { useState, useEffect, useRef } from "react";
import {
  COPY_PREFERENCES_STORAGE_KEY,
  COPY_TOGGLE_KEYS,
  DEFAULT_COPY_PREFERENCES,
  getPresetPreferences,
  PRESET_EVERYTHING,
  PRESET_SPOKEN_ONLY,
  PRESET_CREATOR_HANDOFF,
} from "./constants";
import { serializeScriptForCopy } from "./copySerialization";
import { useToast } from "../../contexts/ToastContext";

function loadStoredPreferences() {
  try {
    const raw = localStorage.getItem(COPY_PREFERENCES_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_COPY_PREFERENCES, ...parsed };
  } catch {
    return null;
  }
}

function savePreferences(prefs) {
  try {
    localStorage.setItem(COPY_PREFERENCES_STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // ignore
  }
}

const CopyScriptPopover = ({ script }) => {
  const { showToast } = useToast();
  const [open, setOpen] = useState(false);
  const [preferences, setPreferences] = useState(() => loadStoredPreferences() ?? { ...DEFAULT_COPY_PREFERENCES });
  const anchorRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const stored = loadStoredPreferences();
    if (stored) setPreferences(stored);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (anchorRef.current && !anchorRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handleToggle = (key) => {
    const next = { ...preferences, [key]: !preferences[key] };
    setPreferences(next);
    savePreferences(next);
  };

  const handlePreset = (preset) => {
    const next = getPresetPreferences(preset);
    setPreferences(next);
    savePreferences(next);
  };

  const matchesPreset = (preset) => {
    const p = getPresetPreferences(preset);
    return Object.keys(COPY_TOGGLE_KEYS).every((k) => preferences[COPY_TOGGLE_KEYS[k]] === p[COPY_TOGGLE_KEYS[k]]);
  };
  const isEverything = matchesPreset(PRESET_EVERYTHING);
  const isSpokenOnly = matchesPreset(PRESET_SPOKEN_ONLY);
  const isCreatorHandoff = matchesPreset(PRESET_CREATOR_HANDOFF);

  const scenes = Array.isArray(script?.script_data?.scenes) ? script.script_data.scenes : [];
  const sceneCount = scenes.length;
  const summaryParts = [];
  if (preferences[COPY_TOGGLE_KEYS.title]) summaryParts.push("title");
  if (preferences[COPY_TOGGLE_KEYS.description]) summaryParts.push("description");
  if (preferences[COPY_TOGGLE_KEYS.direction]) summaryParts.push("direction");
  if (preferences[COPY_TOGGLE_KEYS.masterCaption]) summaryParts.push("caption");
  if (preferences[COPY_TOGGLE_KEYS.sceneSpoken]) summaryParts.push("spoken");
  if (preferences[COPY_TOGGLE_KEYS.sceneCaptions]) summaryParts.push("captions");
  if (preferences[COPY_TOGGLE_KEYS.sceneVisualNotes]) summaryParts.push("visual notes");
  if (preferences[COPY_TOGGLE_KEYS.url]) summaryParts.push("url");
  const summary =
    summaryParts.length > 0
      ? `Includes: ${summaryParts.join(", ")}${sceneCount > 0 ? `, ${sceneCount} scenes` : ""}`
      : "Nothing selected";

  const Toggle = ({ keyName, label }) => (
    <label className="flex items-center gap-2 cursor-pointer text-sm text-theme-text">
      <input
        type="checkbox"
        checked={!!preferences[keyName]}
        onChange={() => handleToggle(keyName)}
        className="rounded border-theme-secondary"
      />
      {label}
    </label>
  );

  const PresetBtn = ({ preset, label }) => {
    const active = preset === PRESET_EVERYTHING ? isEverything : preset === PRESET_SPOKEN_ONLY ? isSpokenOnly : isCreatorHandoff;
    return (
      <button
        type="button"
        onClick={() => handlePreset(preset)}
        className={`px-2.5 py-1 rounded text-xs transition-colors focus:outline-none focus:ring-2 focus:ring-theme-primary ${
          active
            ? "bg-theme-secondary border border-theme-secondary text-theme-text"
            : "border border-theme-secondary text-theme-text opacity-80 hover:bg-theme-secondary hover:opacity-100"
        }`}
      >
        {label}
      </button>
    );
  };

  const doCopy = () => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    const text = serializeScriptForCopy(script, preferences, url);
    navigator.clipboard.writeText(text).then(() => {
      showToast("Copied!");
    });
  };

  const handleCopySelected = () => {
    doCopy();
    setOpen(false);
  };

  return (
    <div className="relative" ref={anchorRef}>
      <button
        type="button"
        onClick={() => {
          doCopy();
          setOpen((v) => !v);
        }}
        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded border text-sm focus:outline-none focus:ring-2 focus:ring-theme-primary ${
          open
            ? "border-theme-primary text-theme-primary bg-theme-secondary"
            : "border-theme-secondary text-theme-text hover:bg-theme-secondary"
        }`}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label="Copy content"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
        <span>Copy</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 z-30 w-80 rounded-lg border border-theme-secondary bg-theme-main shadow-lg p-4">
          <p className="text-sm font-medium text-theme-text mb-3">Copy content</p>

          <div className="space-y-3 mb-4">
            <div>
              <p className="text-[10px] font-medium text-theme-text opacity-55 uppercase tracking-wider mb-1.5">Script</p>
              <div className="space-y-1.5 pl-0">
                <Toggle keyName={COPY_TOGGLE_KEYS.title} label="Title" />
                <Toggle keyName={COPY_TOGGLE_KEYS.description} label="Description" />
                <Toggle keyName={COPY_TOGGLE_KEYS.direction} label="Direction" />
                <Toggle keyName={COPY_TOGGLE_KEYS.masterCaption} label="Master caption" />
              </div>
            </div>
            <div>
              <p className="text-[10px] font-medium text-theme-text opacity-55 uppercase tracking-wider mb-1.5">Scenes</p>
              <div className="space-y-1.5 pl-0">
                <Toggle keyName={COPY_TOGGLE_KEYS.sceneSpoken} label="Scene spoken lines" />
                <Toggle keyName={COPY_TOGGLE_KEYS.sceneCaptions} label="Scene captions" />
                <Toggle keyName={COPY_TOGGLE_KEYS.sceneVisualNotes} label="Scene visual notes" />
              </div>
            </div>
            <div>
              <p className="text-[10px] font-medium text-theme-text opacity-55 uppercase tracking-wider mb-1.5">Extras</p>
              <div className="space-y-1.5 pl-0">
                <Toggle keyName={COPY_TOGGLE_KEYS.url} label="Current page URL" />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-3">
            <PresetBtn preset={PRESET_EVERYTHING} label="Everything" />
            <PresetBtn preset={PRESET_SPOKEN_ONLY} label="Spoken" />
            <PresetBtn preset={PRESET_CREATOR_HANDOFF} label="Creator handoff" />
          </div>

          <p className="text-xs text-theme-text opacity-70 mb-3 truncate" title={summary}>
            {summary}
          </p>

          <button
            type="button"
            onClick={handleCopySelected}
            className="w-full py-2 rounded bg-theme-primary text-theme-button text-sm font-medium hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-theme-primary"
          >
            Copy selected
          </button>
        </div>
      )}
    </div>
  );
};

export default CopyScriptPopover;
